import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Calculator, Loader2, Sparkles } from "lucide-react";

const AREA_PRESETS = [600, 1000, 1200, 1500, 2000, 2400];
const PLOT_PRESETS = [
  { id: "20x30", label: "20×30", area: 600 },
  { id: "30x40", label: "30×40", area: 1200 },
  { id: "30x50", label: "30×50", area: 1500 },
  { id: "40x60", label: "40×60", area: 2400 },
  { id: "50x80", label: "50×80", area: 4000 },
  { id: "60x90", label: "60×90", area: 5400 },
];
const FLOOR_LABELS = ["Ground", "G+1", "G+2", "G+3", "G+4", "G+5"];

export const CalculatorForm = ({ config, form, setForm, onCalculate, loading }) => {
  if (!config) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const coverage = form.ground_coverage ?? 65;
  const applyPlot = (area) =>
    setForm((f) => {
      const cov = f.ground_coverage ?? 65;
      return { ...f, plot_area: area, builtup_area: Math.round((area * cov) / 100) };
    });
  const setCoverage = (v) =>
    setForm((f) =>
      f.plot_area
        ? { ...f, ground_coverage: v, builtup_area: Math.round((f.plot_area * v) / 100) }
        : { ...f, ground_coverage: v }
    );

  return (
    <Card
      className="bg-slate-900/70 border-slate-800 rounded-2xl p-6 sm:p-8 space-y-7"
      data-testid="calculator-form"
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-orange-500/15 grid place-items-center">
          <Calculator className="h-5 w-5 text-orange-400" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-bold text-white">Project Details</h2>
          <p className="text-sm text-slate-400">Fill in your building specs to estimate</p>
        </div>
      </div>

      {/* Built-up area */}
      <div className="space-y-3">
        <Label className="text-slate-200 font-semibold">Built-up Area (per floor)</Label>
        <div className="relative">
          <Input
            type="number"
            min={100}
            value={form.builtup_area}
            onChange={(e) => set("builtup_area", Number(e.target.value))}
            data-testid="input-builtup-area"
            className="bg-slate-950 border-slate-700 h-12 text-white text-lg font-mono-data pr-16 focus-visible:ring-orange-500"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-slate-500 font-medium">
            sq.ft
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {AREA_PRESETS.map((a) => (
            <button
              key={a}
              onClick={() => set("builtup_area", a)}
              data-testid={`preset-area-${a}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                form.builtup_area === a
                  ? "bg-orange-500 text-white"
                  : "bg-slate-800 text-slate-300 hover:bg-slate-700"
              }`}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      {/* Plot size + ground coverage */}
      <div className="space-y-3">
        <Label className="text-slate-200 font-semibold">
          Common Plot Sizes{" "}
          <span className="text-xs font-normal text-slate-500">(sets area via ground coverage)</span>
        </Label>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {PLOT_PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => applyPlot(p.area)}
              data-testid={`plot-${p.id}`}
              className={`flex flex-col items-center py-2 rounded-lg transition-all ${
                form.plot_area === p.area
                  ? "bg-orange-500 text-white shadow-lg shadow-orange-500/20"
                  : "bg-slate-800 text-slate-300 hover:bg-slate-700"
              }`}
            >
              <span className="text-sm font-bold">{p.label}</span>
              <span className="text-[10px] opacity-80 font-mono-data">{p.area} sq.ft</span>
            </button>
          ))}
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-slate-200 font-semibold">Ground Coverage</Label>
            <span className="font-mono-data text-orange-400 font-bold" data-testid="coverage-value">
              {coverage}%
            </span>
          </div>
          <Slider
            value={[coverage]}
            min={40}
            max={90}
            step={1}
            onValueChange={(v) => setCoverage(v[0])}
            data-testid="slider-coverage"
            className="[&_[role=slider]]:bg-orange-500 [&_[role=slider]]:border-orange-500 [&>span:first-child_span]:bg-orange-500"
          />
          <p className="text-xs text-slate-500" data-testid="coverage-hint">
            {form.plot_area
              ? `${form.plot_area} sq.ft plot × ${coverage}% ≈ ${form.builtup_area} sq.ft built-up per floor`
              : "Pick a plot size, then adjust coverage to set a realistic per-floor built-up area."}
          </p>
        </div>
      </div>

      {/* Floors */}
      <div className="space-y-3">
        <Label className="text-slate-200 font-semibold">Number of Floors</Label>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {FLOOR_LABELS.map((lbl, i) => (
            <button
              key={i}
              onClick={() => set("floors", i + 1)}
              data-testid={`floor-${i + 1}`}
              className={`py-2.5 rounded-lg text-sm font-semibold transition-all ${
                form.floors === i + 1
                  ? "bg-orange-500 text-white shadow-lg shadow-orange-500/20"
                  : "bg-slate-800 text-slate-300 hover:bg-slate-700"
              }`}
            >
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {/* Quality tier */}
      <div className="space-y-3">
        <Label className="text-slate-200 font-semibold">Construction Quality</Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {config.quality_tiers.map((q) => (
            <button
              key={q.id}
              onClick={() => set("quality", q.id)}
              data-testid={`quality-${q.id}`}
              className={`text-left p-4 rounded-xl border transition-all ${
                form.quality === q.id
                  ? "border-orange-500 bg-orange-500/10"
                  : "border-slate-700 bg-slate-950 hover:border-slate-600"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-heading font-bold text-white">{q.name}</span>
                {form.quality === q.id && <Sparkles className="h-4 w-4 text-orange-400" />}
              </div>
              <span className="font-mono-data text-xs text-orange-400">₹{q.base_rate}/sq.ft</span>
              <p className="mt-1 text-[11px] text-slate-400 leading-snug">{q.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* City + Foundation */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-200 font-semibold">City</Label>
          <Select value={form.city} onValueChange={(v) => set("city", v)}>
            <SelectTrigger
              data-testid="select-city"
              className="bg-slate-950 border-slate-700 h-11 text-white focus:ring-orange-500"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-white max-h-72">
              {config.cities.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`city-opt-${c.id}`}>
                  {c.name} · {c.tier}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-200 font-semibold">Foundation Type</Label>
          <Select value={form.foundation} onValueChange={(v) => set("foundation", v)}>
            <SelectTrigger
              data-testid="select-foundation"
              className="bg-slate-950 border-slate-700 h-11 text-white focus:ring-orange-500"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-white">
              {config.foundation_types.map((f) => (
                <SelectItem key={f.id} value={f.id} data-testid={`foundation-opt-${f.id}`}>
                  {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Flooring */}
      <div className="space-y-2">
        <Label className="text-slate-200 font-semibold">Flooring Finish</Label>
        <Select value={form.flooring} onValueChange={(v) => set("flooring", v)}>
          <SelectTrigger
            data-testid="select-flooring"
            className="bg-slate-950 border-slate-700 h-11 text-white focus:ring-orange-500"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-white">
            {config.flooring_types.map((f) => (
              <SelectItem key={f.id} value={f.id} data-testid={`flooring-opt-${f.id}`}>
                {f.name}
                {f.add_per_sqft > 0 ? ` (+₹${f.add_per_sqft}/sq.ft)` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Add-ons */}
      <div className="space-y-3">
        <Label className="text-slate-200 font-semibold">Premium Add-ons</Label>
        <div className="space-y-2.5">
          {[
            { k: "false_ceiling", label: "False Ceiling", note: "+₹60/sq.ft" },
            { k: "modular_kitchen", label: "Modular Kitchen", note: "+₹1.5 L" },
            { k: "smart_home", label: "Smart Home Automation", note: "+₹2.0 L" },
          ].map((a) => (
            <div
              key={a.k}
              className="flex items-center justify-between rounded-xl border border-slate-700 bg-slate-950 px-4 py-3"
            >
              <div>
                <p className="text-sm font-medium text-white">{a.label}</p>
                <p className="text-xs text-slate-500 font-mono-data">{a.note}</p>
              </div>
              <Switch
                checked={form[a.k]}
                onCheckedChange={(v) => set(a.k, v)}
                data-testid={`switch-${a.k}`}
                className="data-[state=checked]:bg-orange-500"
              />
            </div>
          ))}
        </div>
      </div>

      <Button
        onClick={onCalculate}
        disabled={loading || !form.builtup_area}
        data-testid="btn-calculate-cost"
        className="w-full h-13 py-6 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold text-base rounded-xl gap-2 shadow-lg shadow-orange-500/25"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Calculator className="h-5 w-5" />}
        Calculate Estimate
      </Button>
    </Card>
  );
};
